import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { useLang } from "@/i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-steel/60 transition-colors focus:border-electric focus:outline-none";

export default function ContactForm() {
  const { t, link } = useLang();
  const [data, setData] = useState({ name: "", company: "", email: "", phone: "", website: "", services: [], message: "", privacy: false, hp: "" });
  const [status, setStatus] = useState("idle");
  const services = t("form.services");

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
  const toggleService = (s) =>
    setData((d) => ({ ...d, services: d.services.includes(s) ? d.services.filter((x) => x !== s) : [...d.services, s] }));

  const valid = data.name.trim().length >= 2 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email) && data.privacy;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || status === "loading") return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, data, { timeout: 12000 });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="contact-success" className="rounded-2xl border border-emerald-400/40 bg-emerald-400/10 p-10 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
        <p className="mt-5 font-display text-lg font-bold">{t("form.success")}</p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} noValidate className="space-y-5">
      <input type="text" value={data.hp} onChange={set("hp")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className="mb-2 block text-xs font-semibold tracking-wider text-steel">{t("form.name")} *</label>
          <input id="cf-name" data-testid="cf-name" required value={data.name} onChange={set("name")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-company" className="mb-2 block text-xs font-semibold tracking-wider text-steel">{t("form.company")}</label>
          <input id="cf-company" data-testid="cf-company" value={data.company} onChange={set("company")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-2 block text-xs font-semibold tracking-wider text-steel">{t("form.email")} *</label>
          <input id="cf-email" data-testid="cf-email" type="email" required value={data.email} onChange={set("email")} className={inputCls} />
        </div>
        <div>
          <label htmlFor="cf-phone" className="mb-2 block text-xs font-semibold tracking-wider text-steel">{t("form.phone")}</label>
          <input id="cf-phone" data-testid="cf-phone" type="tel" value={data.phone} onChange={set("phone")} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cf-website" className="mb-2 block text-xs font-semibold tracking-wider text-steel">{t("form.website")}</label>
          <input id="cf-website" data-testid="cf-website" value={data.website} onChange={set("website")} placeholder="https://" className={inputCls} />
        </div>
      </div>

      <fieldset>
        <legend className="mb-3 text-xs font-semibold tracking-wider text-steel">{t("form.servicesLabel")}</legend>
        <div className="flex flex-wrap gap-2.5">
          {services.map((s) => {
            const on = data.services.includes(s);
            return (
              <button
                type="button"
                key={s}
                data-testid={`cf-service-${s.toLowerCase().replace(/[\s/]+/g, "-")}`}
                onClick={() => toggleService(s)}
                aria-pressed={on}
                className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
                  on ? "border-electric bg-gradient-to-r from-dblue to-electric text-white" : "border-white/15 bg-white/5 text-steel hover:text-white"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div>
        <label htmlFor="cf-message" className="mb-2 block text-xs font-semibold tracking-wider text-steel">{t("form.message")}</label>
        <textarea id="cf-message" data-testid="cf-message" rows={4} value={data.message} onChange={set("message")} className={`${inputCls} resize-none`} />
      </div>

      <label className="flex items-start gap-3 text-xs text-steel leading-relaxed cursor-pointer">
        <input
          type="checkbox"
          data-testid="cf-privacy"
          checked={data.privacy}
          onChange={(e) => setData((d) => ({ ...d, privacy: e.target.checked }))}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-white/5 accent-[#146EF5]"
        />
        <span>{t("form.privacy")}</span>
      </label>

      {status === "error" && (
        <p data-testid="contact-error" className="rounded-xl border border-red-400/40 bg-red-400/10 px-4 py-3 text-sm text-red-300">{t("form.error")}</p>
      )}

      <button
        type="submit"
        data-testid="cf-submit"
        disabled={!valid || status === "loading"}
        className="inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-dblue to-electric px-8 py-4 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(33,167,255,0.4)] disabled:opacity-40 disabled:hover:scale-100 disabled:hover:shadow-none"
      >
        {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {status === "loading" ? t("form.sending") : t("form.submit")}
      </button>
    </form>
  );
}
